import {
  getEntityBounds,
  heavyPolylineDrawnBounds,
  type DimensionStyleTable,
} from "@dxf-ai-editor/cad-core";
import {
  coerceColor,
  colorToLegacyString,
  tableAsBlockReference,
} from "@dxf-ai-editor/drawing-model";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { blockInsertMetadataBounds, type BlockTable } from "./blockExtentsCache";

/**
 * # Entity Metadata Index (PR-V1 / Phase 3)
 *
 * Lightweight per-entity record (~80 B) keeping id/type/layer/color/
 * bounds/spaceId. Sits alongside the non-reactive `__entitiesCache` in
 * `editorStore.ts` and lets the spatial index, renderer and panels
 * identify/locate any entity without keeping full DrawingEntity payloads
 * in React state. Foundation for PR-V2 (cache-direct render), PR-V3
 * (lift cap), PR-V4 (panels virtualization). Pure data — no React, no
 * zustand. Aggregates kept fresh on every mutation; `bulkLoad` recomputes
 * from scratch, `set`/`delete` update incrementally. `markBoundsStale` /
 * `isBoundsStale` is wired but unused in PR-V1 (consumed by PR-V2).
 *
 * **Phase 3 — dict bridge.** `layer` and `color` strings were previously
 * stored verbatim per record (~11 MB duplication on 700k entries). The
 * index now interns them through an internal `SharedStringTable` and
 * stores numeric indices on each record. Public `EntityMetadata` shape
 * is preserved — `get`/`values`/`filterBySpace` return records with the
 * deref'd strings. Consumers that already accept the public shape need
 * no changes. New accessors `getLayerName(metadata)` /
 * `getColorName(metadata)` exist for forward-compat: they take a public
 * record and return the layer/color string, allowing future migrations
 * to expose the raw indices without breaking call sites.
 */

export interface EntityMetadata {
  id: string;
  /** Entity type as written by the model (LINE, CIRCLE, MTEXT, ...). */
  type: string;
  layer: string;
  /** hex color or "BYLAYER"/"BYBLOCK" sentinel; `null` if absent. */
  color: string | null;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  /** model | layout id. */
  spaceId: string;
  /**
   * Phase 2 Wave 2 Task 3 — geometry revision counter.
   *
   * Monotonically bumped by `commitMutation` when an `updated[i]`
   * mutation has `changedFields` of `"geometry"` or `"both"`. The
   * renderer's tessellation cache (`adapters/tessellationCache.ts`)
   * keys its entries on `(id, geomRev)` so a non-bumped style-only
   * change reuses the cached vertex buffer.
   *
   * Default `0` on fresh inserts. Reads MUST treat `undefined` as `0`
   * for backwards compatibility with any pre-Wave-2 caller that
   * constructs an EntityMetadata without setting this field.
   */
  geomRev?: number;
}

export interface EntityMetadataAggregates {
  /** layer name → entity count. */
  byLayer: Map<string, number>;
  /** type → count. */
  byType: Map<string, number>;
  total: number;
  totalBounds: EntityMetadata["bounds"] | null;
}

const ZERO_BOUNDS: EntityMetadata["bounds"] = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

function isFiniteBounds(b: EntityMetadata["bounds"]): boolean {
  return (
    Number.isFinite(b.minX) &&
    Number.isFinite(b.minY) &&
    Number.isFinite(b.maxX) &&
    Number.isFinite(b.maxY)
  );
}

/**
 * Phase 3 — internal record. `layer` / `color` are unsigned integer
 * indices into `EntityMetadataIndex.stringTable`. The table is local
 * to this index (not the compactStores `SharedStringTable`) so the
 * 16-bit cap there does not apply — drawings with extreme layer or
 * truecolor cardinality continue to index correctly.
 *
 * The `color` field uses `COLOR_IDX_NULL = -1` to distinguish a real
 * `color: null` (per the schema, a valid value) from an empty-string
 * color (`""`, also a valid value). Index 0 is the reserved
 * empty-string slot — round-trip of `color: ""` and `color: null`
 * are now distinct.
 *
 * `type` stays a string (low cardinality — ~20 distinct values across
 * the codebase, dedup via the shared table is overkill for the savings).
 */
interface InternalEntityMetadata {
  id: string;
  type: string;
  layerIdx: number;
  /** `COLOR_IDX_NULL` (-1) means `color: null`; non-negative is a real interned color. */
  colorIdx: number;
  bounds: EntityMetadata["bounds"];
  spaceId: string;
}

/** Sentinel for `color: null` — distinct from the index-0 "" slot. */
const COLOR_IDX_NULL = -1;

/**
 * Local string interner used by `EntityMetadataIndex`. Independent of
 * the compactStores `SharedStringTable` (which caps at 0xffff) — this
 * one has no hard cap; `Map` + array entries are bounded only by
 * available memory. Index 0 is reserved for the empty string so
 * downstream consumers can treat 0 as "" without an extra branch.
 *
 * Phase 3 followup (codex P2): the table now reference-counts each
 * interned string. `set()` / `delete()` / `applyBatch()` increment on
 * intern, decrement on overwrite/remove; when refcount hits zero the
 * entry is dropped from the lookup map so subsequent
 * `internedStringCount()` reflects only live strings. The reserved
 * empty-string slot (id 0) is never decremented — it acts as the
 * sentinel for the "" value.
 *
 * Trade-off: freed slots in `strings` are marked `undefined` but never
 * reclaimed inside the array (sparse growth). `bulkLoad()` recreates
 * the table from scratch so long-running sessions only see sparse
 * growth between bulk loads, not unbounded growth of distinct strings.
 */
interface IndexStringTable {
  /**
   * Backing array indexed by interned-id. Slot 0 is permanently `""`
   * (the empty-string sentinel). Other slots hold the interned value
   * while refcount > 0, and are set to `undefined` after release so
   * subsequent lookups return "" via the nullish coalescing fallback.
   */
  strings: (string | undefined)[];
  /** value → id for live strings only. Released values are deleted. */
  readonly index: Map<string, number>;
  /** refcount per slot; mirrors `strings.length`. Slot 0 is unused. */
  refs: number[];
  /** Number of live (refcount > 0) entries including the sentinel. */
  liveCount: number;
}
function createIndexStringTable(): IndexStringTable {
  return {
    strings: [""],
    index: new Map([["", 0]]),
    // Slot 0 is the empty-string sentinel — its refcount is never
    // touched by intern/release, so the initial value here is a marker
    // only (the sentinel branch in `indexRelease` short-circuits).
    refs: [1],
    liveCount: 1,
  };
}
function indexIntern(table: IndexStringTable, value: string): number {
  const existing = table.index.get(value);
  if (existing !== undefined) {
    // Slot 0 (empty string) is the sentinel — refcount stays at its
    // initial value and is not bumped on additional interns. This
    // keeps the sentinel pinned regardless of caller activity.
    if (existing !== 0) table.refs[existing] = (table.refs[existing] ?? 0) + 1;
    return existing;
  }
  const id = table.strings.length;
  table.strings.push(value);
  table.refs.push(1);
  table.index.set(value, id);
  table.liveCount += 1;
  return id;
}
/**
 * Phase 3 followup (codex P2) — decrement refcount for the interned id.
 * When refcount drops to zero the entry is removed from the lookup map
 * and its `strings` slot is cleared to `undefined`. The empty-string
 * sentinel (id 0) is a no-op so the "" → 0 mapping survives every
 * release.
 *
 * Negative-ref / unknown-id calls are silent no-ops (defensive: callers
 * may release `COLOR_IDX_NULL` indirectly through the `colorIdx`
 * pathway — see `releaseInternalColor`).
 */
function indexRelease(table: IndexStringTable, id: number): void {
  if (id <= 0) return; // sentinel or invalid → no-op
  const next = (table.refs[id] ?? 0) - 1;
  if (next > 0) {
    table.refs[id] = next;
    return;
  }
  if (next < 0) return; // already released — defensive guard
  table.refs[id] = 0;
  const value = table.strings[id];
  if (value !== undefined) {
    table.index.delete(value);
    table.strings[id] = undefined;
  }
  table.liveCount -= 1;
}
function indexLookup(table: IndexStringTable, id: number): string {
  return table.strings[id] ?? "";
}

export class EntityMetadataIndex {
  private readonly records = new Map<string, InternalEntityMetadata>();
  private readonly byLayerCounts = new Map<string, number>();
  private readonly byTypeCounts = new Map<string, number>();
  private readonly staleBoundsIds = new Set<string>();
  private totalBoundsCache: EntityMetadata["bounds"] | null = null;
  /**
   * Phase 3 — dictionary-encoded layer/color strings. Lives inside the
   * index so each `clear()` releases the table along with the records.
   * Note: indices grow monotonically — `clear()` resets the table to
   * empty; do not assume index stability across `clear()` calls.
   *
   * Uses a local `IndexStringTable` rather than the compactStores
   * `SharedStringTable` because the latter caps at 0xffff distinct
   * strings. Drawings with extreme layer / truecolor cardinality
   * (which arise in real CAD work) must continue to index correctly.
   */
  private stringTable: IndexStringTable = createIndexStringTable();

  /** O(1) lookup by id. */
  get(id: string): EntityMetadata | undefined {
    const internal = this.records.get(id);
    if (internal === undefined) return undefined;
    return this.toPublic(internal);
  }

  /** Update or insert single record. Recomputes aggregates incrementally. */
  set(metadata: EntityMetadata): void {
    const internal = this.toInternal(metadata);
    const previous = this.records.get(metadata.id);
    if (previous) {
      this.subtractCountsInternal(previous);
      // Phase 3 followup (codex P2) — release the previous record's
      // interned strings BEFORE overwriting. Without this, repeated
      // upserts that change layer/color leak strings into the table
      // until the next `clear()`/`bulkLoad()`. Order matters: release
      // before `set()` below so the slot for the new value is intern'd
      // against the post-release table state (lets the new value reuse
      // an existing slot if it matched the released one, although the
      // common case is distinct values).
      this.releaseInternalStrings(previous);
    }
    this.records.set(metadata.id, internal);
    this.addCountsInternal(internal);
    // Pure insert can extend bounds in O(1); replace requires a full
    // recompute (previous record may have been the sole extreme).
    if (previous) this.recomputeTotalBounds();
    else this.extendTotalBounds(internal.bounds);
  }

  /** Remove single record. Recomputes aggregates incrementally. */
  delete(id: string): boolean {
    const previous = this.records.get(id);
    if (!previous) return false;
    this.records.delete(id);
    this.subtractCountsInternal(previous);
    // Phase 3 followup (codex P2) — release the removed record's
    // interned strings so unique values don't accumulate across long
    // editing sessions (e.g. color-slider edits that produce many
    // distinct hex values, each used briefly).
    this.releaseInternalStrings(previous);
    this.staleBoundsIds.delete(id);
    this.recomputeTotalBounds();
    return true;
  }

  /**
   * GATE 3 (Track 1 / Phase 1) — batched upsert + delete in O(|batch| + N).
   *
   * Per-record `set()`/`delete()` each call `recomputeTotalBounds()` —
   * O(N) walk of every record. A reducer that touches 10k entities in a
   * 100k-base index pays 10k × 100k = 1B ops via the loop API; the
   * existing perf-budget test grants 30s for 1k ops on 100k base.
   *
   * `applyBatch` keeps aggregate maintenance per-record but defers the
   * total-bounds recompute:
   *   - If any delete OR any upsert that overwrote a previous record →
   *     single full `recomputeTotalBounds()` at the end (O(N), once).
   *   - Else (pure inserts) → extend cached bounds in O(|inserts|).
   *
   * Inputs are pre-computed `EntityMetadata` records — caller (commitMutation)
   * derives them from `DrawingEntity` upstream so this method stays
   * pure-data and agnostic of the model schema.
   *
   * `staleBoundsIds` are cleared for every deleted id (matches `delete()`),
   * and untouched for upserts (callers that need staleness call
   * `markBoundsStale` explicitly — same as the per-record API).
   */
  applyBatch(input: {
    upserts: ReadonlyArray<EntityMetadata>;
    deletes: ReadonlyArray<string>;
  }): void {
    let needsFullRecompute = false;

    for (const id of input.deletes) {
      const previous = this.records.get(id);
      if (!previous) continue;
      this.records.delete(id);
      this.subtractCountsInternal(previous);
      // Phase 3 followup (codex P2) — mirror `delete()` per-record path.
      this.releaseInternalStrings(previous);
      this.staleBoundsIds.delete(id);
      needsFullRecompute = true;
    }

    const pureInserts: InternalEntityMetadata[] = [];
    for (const metadata of input.upserts) {
      const internal = this.toInternal(metadata);
      const previous = this.records.get(metadata.id);
      if (previous) {
        this.subtractCountsInternal(previous);
        // Phase 3 followup (codex P2) — mirror `set()` overwrite path.
        this.releaseInternalStrings(previous);
        this.records.set(metadata.id, internal);
        this.addCountsInternal(internal);
        needsFullRecompute = true;
      } else {
        this.records.set(metadata.id, internal);
        this.addCountsInternal(internal);
        pureInserts.push(internal);
      }
    }

    if (needsFullRecompute) {
      this.recomputeTotalBounds();
      return;
    }
    for (const internal of pureInserts) {
      this.extendTotalBounds(internal.bounds);
    }
  }

  /** Bulk replace (used at import). Recomputes aggregates from scratch. */
  bulkLoad(records: ReadonlyArray<EntityMetadata>): void {
    this.records.clear();
    this.byLayerCounts.clear();
    this.byTypeCounts.clear();
    this.staleBoundsIds.clear();
    // Phase 3 — reset the dictionary too; old indices are not valid in
    // the next load. `createIndexStringTable` reserves slot 0 for "".
    this.stringTable = createIndexStringTable();

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let any = false;

    for (const record of records) {
      const internal = this.toInternal(record);
      this.records.set(record.id, internal);
      this.addCountsInternal(internal);
      const b = record.bounds;
      if (!isFiniteBounds(b)) continue;
      if (b.minX < minX) minX = b.minX;
      if (b.minY < minY) minY = b.minY;
      if (b.maxX > maxX) maxX = b.maxX;
      if (b.maxY > maxY) maxY = b.maxY;
      any = true;
    }

    this.totalBoundsCache = any ? { minX, minY, maxX, maxY } : null;
  }

  get size(): number {
    return this.records.size;
  }

  /** Iterate all records (for spatial-index bulk load). */
  *values(): IterableIterator<EntityMetadata> {
    for (const internal of this.records.values()) {
      yield this.toPublic(internal);
    }
  }

  /** Snapshot of current aggregates. Maps are copies — safe to mutate. */
  aggregates(): EntityMetadataAggregates {
    return {
      byLayer: new Map(this.byLayerCounts),
      byType: new Map(this.byTypeCounts),
      total: this.records.size,
      totalBounds: this.totalBoundsCache ? { ...this.totalBoundsCache } : null,
    };
  }

  filterBySpace(spaceId: string): EntityMetadata[] {
    const out: EntityMetadata[] = [];
    for (const internal of this.records.values()) {
      if (internal.spaceId === spaceId) out.push(this.toPublic(internal));
    }
    return out;
  }

  /**
   * Every record of one entity `type` (e.g. `"Block"`), publicly deref'd.
   * One pass over the internal records with a string compare — only the
   * matches pay `toPublic`, so scanning a 685k-entity index for its few
   * hundred INSERT records allocates only those. Used by the block-
   * redefinition metadata refresh (`blockRedefinitionMetadataRefresh.ts`) to
   * find the INSERT records whose derived bounds a block-table change
   * invalidates.
   */
  filterByType(type: string): EntityMetadata[] {
    const out: EntityMetadata[] = [];
    for (const internal of this.records.values()) {
      if (internal.type === type) out.push(this.toPublic(internal));
    }
    return out;
  }

  markBoundsStale(id: string): void {
    if (this.records.has(id)) this.staleBoundsIds.add(id);
  }

  isBoundsStale(id: string): boolean {
    return this.staleBoundsIds.has(id);
  }

  clear(): void {
    this.records.clear();
    this.byLayerCounts.clear();
    this.byTypeCounts.clear();
    this.staleBoundsIds.clear();
    this.totalBoundsCache = null;
    this.stringTable = createIndexStringTable();
  }

  /**
   * Phase 3 — exposed for diagnostic / accessor use. Returns the layer
   * name string for an `EntityMetadata` record produced by this index.
   * When `metadata.layer` is already a string (current contract), this
   * is a no-op identity. Provided to give consumers a stable accessor
   * that survives any future migration to expose raw indices on the
   * public type.
   */
  getLayerName(metadata: EntityMetadata): string {
    return metadata.layer;
  }

  /** Phase 3 — counterpart of `getLayerName` for the color field. */
  getColorName(metadata: EntityMetadata): string | null {
    return metadata.color;
  }

  /**
   * Phase 3 — diagnostics. Number of LIVE (refcount > 0) entries in the
   * internal string table, including the reserved empty-string sentinel.
   * Useful for tests verifying dedupe + reclamation behaviour without
   * exposing the table itself.
   *
   * Phase 3 followup (codex P2): post-fix this no longer reflects
   * `strings.length` — that array grows monotonically and contains
   * `undefined` for released slots. `liveCount` tracks the number of
   * value→id mappings the lookup map currently holds, so callers see
   * the size of the "logical" interning state.
   */
  internedStringCount(): number {
    return this.stringTable.liveCount;
  }

  private toInternal(metadata: EntityMetadata): InternalEntityMetadata {
    return {
      id: metadata.id,
      type: metadata.type,
      layerIdx: indexIntern(this.stringTable, metadata.layer),
      colorIdx:
        metadata.color === null
          ? COLOR_IDX_NULL
          : indexIntern(this.stringTable, metadata.color),
      bounds: metadata.bounds,
      spaceId: metadata.spaceId,
    };
  }

  /**
   * Phase 3 followup (codex P2) — decrement refcounts for the layer +
   * color slots referenced by `previous`. Called from `set()` (overwrite
   * branch), `delete()`, and `applyBatch()` so unique strings drop out
   * of the table once the last record holding them goes away.
   *
   * `COLOR_IDX_NULL` (-1) is the dedicated `color: null` sentinel and
   * is not interned — `indexRelease` short-circuits on `id <= 0` so
   * passing it through is a safe no-op.
   */
  private releaseInternalStrings(previous: InternalEntityMetadata): void {
    indexRelease(this.stringTable, previous.layerIdx);
    indexRelease(this.stringTable, previous.colorIdx);
  }

  private toPublic(internal: InternalEntityMetadata): EntityMetadata {
    const layer = indexLookup(this.stringTable, internal.layerIdx);
    // COLOR_IDX_NULL is the dedicated `color: null` sentinel — distinct
    // from index 0 ("") so real empty-string colors round-trip
    // unchanged. Real DrawingEntity color is usually "BYLAYER"/"#hex"
    // but the schema permits "" too.
    const color =
      internal.colorIdx === COLOR_IDX_NULL
        ? null
        : indexLookup(this.stringTable, internal.colorIdx);
    return {
      id: internal.id,
      type: internal.type,
      layer,
      color,
      bounds: internal.bounds,
      spaceId: internal.spaceId,
    };
  }

  private addCountsInternal(internal: InternalEntityMetadata): void {
    const layer = indexLookup(this.stringTable, internal.layerIdx);
    this.byLayerCounts.set(layer, (this.byLayerCounts.get(layer) ?? 0) + 1);
    this.byTypeCounts.set(
      internal.type,
      (this.byTypeCounts.get(internal.type) ?? 0) + 1,
    );
  }

  private subtractCountsInternal(internal: InternalEntityMetadata): void {
    const layer = indexLookup(this.stringTable, internal.layerIdx);
    const lc = this.byLayerCounts.get(layer);
    if (lc !== undefined) {
      if (lc <= 1) this.byLayerCounts.delete(layer);
      else this.byLayerCounts.set(layer, lc - 1);
    }
    const tc = this.byTypeCounts.get(internal.type);
    if (tc !== undefined) {
      if (tc <= 1) this.byTypeCounts.delete(internal.type);
      else this.byTypeCounts.set(internal.type, tc - 1);
    }
  }

  private extendTotalBounds(b: EntityMetadata["bounds"]): void {
    if (!isFiniteBounds(b)) return;
    if (this.totalBoundsCache === null) {
      this.totalBoundsCache = { ...b };
      return;
    }
    const t = this.totalBoundsCache;
    if (b.minX < t.minX) t.minX = b.minX;
    if (b.minY < t.minY) t.minY = b.minY;
    if (b.maxX > t.maxX) t.maxX = b.maxX;
    if (b.maxY > t.maxY) t.maxY = b.maxY;
  }

  private recomputeTotalBounds(): void {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let any = false;
    for (const internal of this.records.values()) {
      const b = internal.bounds;
      if (!isFiniteBounds(b)) continue;
      if (b.minX < minX) minX = b.minX;
      if (b.minY < minY) minY = b.minY;
      if (b.maxX > maxX) maxX = b.maxX;
      if (b.maxY > maxY) maxY = b.maxY;
      any = true;
    }
    this.totalBoundsCache = any ? { minX, minY, maxX, maxY } : null;
  }
}

export function createEntityMetadataIndex(): EntityMetadataIndex {
  return new EntityMetadataIndex();
}

/**
 * Phase 3 — free-function accessor mirroring the class method. Consumers
 * that hold a raw `EntityMetadata` without a back-reference to the
 * `EntityMetadataIndex` (e.g. records passed into `set()` or returned by
 * `get()`) use this to read the layer name through a stable API.
 *
 * Current implementation is a passthrough — the public `EntityMetadata`
 * still carries layer as a string. The accessor exists for forward-
 * compat: a future migration that exposes raw `Uint16` indices on the
 * public type will only need to update this function (and the matching
 * `getColorName`), keeping the 30+ call sites unchanged.
 */
export function getLayerName(metadata: EntityMetadata): string {
  return metadata.layer;
}

/** Phase 3 — color accessor counterpart of `getLayerName`. */
export function getColorName(metadata: EntityMetadata): string | null {
  return metadata.color;
}

/* ── Bounds computation ──────────────────────────────────────────────── */

interface Pt {
  x: number;
  y: number;
}

function fromPoints(points: ReadonlyArray<Pt>): EntityMetadata["bounds"] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const p of points) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
    any = true;
  }
  return any ? { minX, minY, maxX, maxY } : { ...ZERO_BOUNDS };
}

/**
 * Compute axis-aligned bounds for any DrawingEntity. Uses the coordinate
 * fields exposed by each kind. For HATCH/Multileader we walk the enclosing
 * geometry; unknown kinds fall back to zero-size bounds (PR-V2 viewport-
 * cull skips zero-bounded records when frustum-checking).
 *
 * Arc bbox is conservative full-circle (super-set; metadata targets
 * membership queries — exact arc bbox would require quadrant inclusion).
 * The legacy heavy `Polyline` arm is NOT a coordinate hull: it delegates to
 * `cad-core`'s `heavyPolylineDrawnBounds`, because a 2D heavy polyline's
 * vertices are OCS coordinates that must be lifted through the group-210
 * extrusion before they mean anything in this (WCS) box, AND because a
 * width-carrying polyline is drawn as a BAND whose extent its centreline does
 * not contain — see that arm's own comment (@codex PR #785 round 5 FINDING A,
 * round 10).
 * Block INSERT bounds are the referenced definition's child geometry under
 * the INSERT transform (nested INSERTs + MINSERT grid, cad-core
 * `blockGeometry.ts`, memoized per `blocks` table — `blockExtentsCache.ts`)
 * when the block table is supplied; without it (or for a missing / empty /
 * cyclic definition) the INSERT anchors on its insertion point only. The
 * cache-direct render path derives every INSERT stub's screen size from
 * THIS box, so the table must be passed on every production index write.
 *
 * `dimensionStyles` — the document's name-keyed DIMSTYLE table
 * (`blockExtentsCache.ts::dimensionStyleTableFor` over
 * `document.styleTables.dimensionStyles`), used ONLY by the `Block` arm: a
 * definition containing a `Dimension` child is bounded with that child's
 * NAMED style DIMVARs instead of AutoCAD's hardcoded defaults, so the indexed
 * INSERT box covers what the renderer actually draws (Codex PR #784 round 3
 * P1). Same "must be passed on every production index write" rule as `blocks`
 * — omitted, the INSERT box silently under-reports a styled dimension child.
 */
function boundsForEntity(
  entity: DrawingEntity,
  blocks?: BlockTable,
  dimensionStyles?: DimensionStyleTable,
): EntityMetadata["bounds"] {
  switch (entity.type) {
    case "Line":
      return fromPoints([entity.start, entity.end]);
    case "Point":
      return fromPoints([entity.location]);
    case "Circle":
    case "Arc": {
      const { center: c, radius: r } = entity;
      return { minX: c.x - r, minY: c.y - r, maxX: c.x + r, maxY: c.y + r };
    }
    case "Ellipse": {
      const c = entity.center;
      const dx = Math.abs(entity.majorAxisEndpoint.x);
      const dy = Math.abs(entity.majorAxisEndpoint.y);
      return { minX: c.x - dx, minY: c.y - dy, maxX: c.x + dx, maxY: c.y + dy };
    }
    case "Spline":
      return fromPoints([...entity.controlPoints, ...entity.fitPoints]);
    case "LWPOLYLINE": {
      // @codex PR #785 round 10, found by the closure sweep the heavy-POLYLINE
      // arm below prompted: `fromPoints(entity.vertices)` is the CHORD hull. It
      // reads group 10/20 and ignores group 42 entirely, so a bulged span that
      // bows OUTSIDE its chord is indexed as the flat chord — a semicircle on
      // `(0,0) → (10,0)` (bulge 1) was `{0, 0, 10, 0}` for an arc the canvas
      // draws up to `y = 5`. That is the SAME disappearing-geometry shape as
      // the heavy-POLYLINE case below, on the same cache-direct path: a window
      // over the bow alone returns nothing from `spatial.intersect`, so the id
      // is never lifted and the visible arc is gone.
      //
      // `cad-core`'s `getEntityBounds` already owns the exact answer — so this
      // is a REMOVED derivation, not an added one, exactly like the
      // heavy-POLYLINE arm.
      //
      // @codex PR #785 round 11, F1 — that answer was `entityModelBounds` →
      // `lwpolylineBounds`, which frames the CENTRELINE (exactly, arcs
      // included) and reads neither group 43 nor groups 40 / 41. An LWPOLYLINE
      // that declares a width is not stroked along its centreline at all: the
      // canvas draws the same mitred, square-capped BAND it draws for a heavy
      // POLYLINE (`batchedLayer.ts::widePolylineLineVertices`, over the
      // `widths` `typed/lwpolylineFromEntity.ts` emits). So a
      // constant-width-4 LWPOLYLINE on `(0,0) → (10,0)` was indexed at
      // `{0, 0, 10, 0}` — `y ≡ 0` — for a ribbon drawn across `y ∈ [-2, +2]`,
      // and on THIS path that is geometry LOSS, not a slack box, for exactly
      // the reason the `"Polyline"` arm below spells out: a viewport over
      // `y ∈ [1, 3]` cuts only the ribbon, `spatial.intersect` returns nothing,
      // and an id the rbush drops is never lifted. `getEntityBounds` now has
      // its own `"LWPOLYLINE"` arm (`cad-core/lwpolylineDrawnBounds.ts`, the
      // SAME one function `blockDefinitionWalk.ts`' block-child arm calls), so
      // this arm needed no change to follow it — which is the point of
      // delegating rather than deriving.
      // `scene3d/scene/__tests__/cacheDirectWideLwpolylineBand.test.ts` drives
      // it end to end.
      //
      // Deliberately NOT `computeEntityWcsBounds` here, unlike that arm: an
      // LWPOLYLINE's vertices are OCS too, but `scene3d/adapters/typed/
      // lwpolylineFromEntity.ts` does NOT lift them (it feeds raw x/y with
      // `z = elevation`), so the renderer and this index agree TODAY and
      // lifting the box alone would move the record off the geometry that is
      // actually drawn — manufacturing the very bug this arm is closing. The
      // LWPOLYLINE OCS lift is a RENDERER gap and has to be closed there
      // first; see this commit's message for the measurement. It is STILL a
      // declared gap after round 11 — that round closed the WIDTH band, which
      // is authored in the very same unlifted frame, and lifting the box would
      // have moved the record off that band too.
      const box = getEntityBounds(entity);
      if (box === null) return { ...ZERO_BOUNDS };
      return { minX: box.minX, minY: box.minY, maxX: box.maxX, maxY: box.maxY };
    }
    case "Text":
    case "MText":
      return fromPoints([entity.insertionPoint]);
    case "Dimension": {
      const pts: Pt[] = [entity.defPoint, entity.textMidpoint];
      if (entity.extLine1Point) pts.push(entity.extLine1Point);
      if (entity.extLine2Point) pts.push(entity.extLine2Point);
      if (entity.thirdPoint) pts.push(entity.thirdPoint);
      if (entity.arcPoint) pts.push(entity.arcPoint);
      return fromPoints(pts);
    }
    case "Multileader": {
      const pts: Pt[] = [entity.textLocation];
      if (entity.basePoint) pts.push(entity.basePoint);
      for (const root of entity.leaderRoots) {
        for (const node of root.nodes) {
          for (const line of node.lines) {
            for (const v of line.vertices) pts.push(v);
          }
        }
      }
      return fromPoints(pts);
    }
    case "Revision Cloud":
    case "Wipeout":
      return fromPoints(entity.vertices);
    case "Hatch": {
      const pts: Pt[] = [];
      for (const path of entity.boundaryPaths) {
        for (const edge of path.edges) {
          if (edge.edgeType === "line") {
            pts.push(edge.start, edge.end);
          } else if (edge.edgeType === "arc") {
            const { center: c, radius: r } = edge;
            pts.push(
              { x: c.x - r, y: c.y - r },
              { x: c.x + r, y: c.y + r },
            );
          } else {
            const c = edge.center;
            const dx = Math.abs(edge.majorAxisEnd.x);
            const dy = Math.abs(edge.majorAxisEnd.y);
            pts.push(
              { x: c.x - dx, y: c.y - dy },
              { x: c.x + dx, y: c.y + dy },
            );
          }
        }
      }
      return fromPoints(pts);
    }
    case "Block":
      return blockInsertMetadataBounds(entity, blocks, dimensionStyles);
    case "Rectangular Associative Array":
      return fromPoints([entity.basePoint]);
    case "Viewport": {
      const c = entity.center;
      const hw = entity.width / 2;
      const hh = entity.height / 2;
      return { minX: c.x - hw, minY: c.y - hh, maxX: c.x + hw, maxY: c.y + hh };
    }
    case "Polyline": {
      // Legacy heavy POLYLINE — the box of the shape it is DRAWN as, NOT the
      // raw vertex hull and NOT its bare centreline.
      //
      // @codex PR #785 round 5, FINDING A — a 2D heavy polyline's group-10/20
      // vertex values are OBJECT coordinates in the plane of its group-210
      // extrusion, at the entity's ELEVATION along that normal (Autodesk DXF
      // reference, "Object Coordinate Systems (OCS)"). Mapping them into this
      // box verbatim indexed the entity at its UNLIFTED location, while the
      // renderer (`scene3d/adapters/typed/polylineFromEntity.ts`), the picker
      // (`cad-core/selectLeaf.ts`) and the AI OSNAP route
      // (`ai/sceneQuery/osnapQuery.ts`) all place it through
      // `heavyPolylineWcsResolver`. On the cache-direct path (streaming import,
      // DXF >= 2 MB) THIS box is what `SceneBulkLoad` loads into the spatial
      // index, so with any non-default extrusion viewport culling, picking,
      // OSNAP prefiltering and layout fitting queried an empty region while the
      // visible entity sat somewhere else.
      //
      // @codex PR #785 round 10 — that fix reached for `computeEntityWcsBounds`,
      // which is `drawing-model`'s "where is this entity in WCS" truth but is
      // WIDTH-BLIND: it bounds the CENTRELINE (plus its group-39 sweep) and
      // knows nothing about groups 40 / 41. A heavy polyline that declares a
      // width is not stroked along its centreline at all — the canvas
      // (`batchedLayer.ts::widePolylineLineVertices`), the plot and window
      // select all draw and test the mitred, square-capped BAND. So a
      // constant-width-4 polyline on `(0,0) → (10,0)` was indexed at
      // `{0, 0, 10, 0}` — `y ≡ 0` — for a ribbon drawn across `y ∈ [-2, +2]`,
      // and THAT is a disappearing-geometry bug rather than a slack box: a
      // viewport over `y ∈ [1, 3]` sees only the ribbon, `lod/visibleSet.ts
      // computeVisibleSet` starts at `spatial.intersect(viewport)`, and an id
      // the rbush does not return is never lifted — so the batcher that DOES
      // understand widths is never reached and the visible band is simply gone.
      // `scene/__tests__/cacheDirectWidePolylineBand.test.ts` drives exactly
      // that end to end.
      //
      // `cad-core`'s `heavyPolylineDrawnBounds` is the ONE function that already
      // answers this for `getEntityBounds`' `"Polyline"` arm and for
      // `blockDefinitionWalk.ts`' block-child arm (rounds 8 + 9): the drawn
      // silhouette — the band where one is drawn, the tessellated centreline
      // otherwise — swept along the group-39 thickness and UNION'd with the same
      // `entityModelBounds` box `computeEntityWcsBounds` projects. Pointing this
      // arm at it is therefore a strict WIDENING of the previous answer, not a
      // different one: every round-5 property (the Arbitrary-Axis lift, the
      // poly-face FACE-record filter, the exact bulge extrema, the thickness
      // sweep) is inside it unchanged, and the index still decides nothing about
      // heavy-polyline placement itself. It also makes this arm agree with
      // `Block` / `Table` above, which reach the SAME `cad-core` walk through
      // `blockInsertMetadataBounds` — so a wide polyline is indexed identically
      // whether it is top-level or a block child.
      //
      // `null` (no drawn run AND no finite model point — an empty vertex list)
      // keeps the zero-size fallback `fromPoints([])` produced.
      const drawn = heavyPolylineDrawnBounds(entity);
      if (drawn === null) return { ...ZERO_BOUNDS };
      return {
        minX: drawn.minX,
        minY: drawn.minY,
        maxX: drawn.maxX,
        maxY: drawn.maxY,
      };
    }
    case "Table":
      // Block-backed, exactly like `Block` above: a table's extent is the
      // footprint of the anonymous `*T<N>` block that carries its graphics.
      return blockInsertMetadataBounds(
        tableAsBlockReference(entity),
        blocks,
        dimensionStyles,
      );
    case "OLE Frame":
      // The frame rectangle IS the entity's extent — AutoCAD clips the OLE
      // picture to exactly this frame, so this is complete regardless of the
      // deliberately-unrendered embedded raster.
      return fromPoints([entity.upperLeft, entity.lowerRight]);
    default:
      return { ...ZERO_BOUNDS };
  }
}

/**
 * Build EntityMetadata from a `DrawingEntity` (full payload from cache).
 * `blocks` (`DrawingDocument.blocks`) resolves a `Block` (INSERT) entity's
 * real extents — see {@link boundsForEntity}; every production caller
 * (import bulk load, `commitMutation`) passes it, together with
 * `dimensionStyles` (the name-keyed `document.styleTables.dimensionStyles`,
 * built once per array identity by `blockExtentsCache.ts
 * ::dimensionStyleTableFor`) so a `Dimension` child inside the referenced
 * definition contributes its NAMED-style extents (Codex PR #784 round 3 P1).
 */
export function entityMetadataFromDrawingEntity(
  entity: DrawingEntity,
  blocks?: BlockTable,
  dimensionStyles?: DimensionStyleTable,
): EntityMetadata {
  return {
    id: entity.id,
    type: entity.type,
    layer: entity.layer,
    // EPIC-COLOR-UNION S1 — project the typed colour union to the legacy string
    // the metadata index stores (and the spatial-stub renderer reads).
    // `coerceColor` defends against raw entity literals (tests / cache) that
    // bypassed the schema and carry a legacy-string or absent `color`; the model
    // default ({kind:"byLayer"}) projects to "ByLayer" — byte-identical.
    color: colorToLegacyString(coerceColor(entity.color)),
    bounds: boundsForEntity(entity, blocks, dimensionStyles),
    spaceId: entity.spaceId,
  };
}
